import { useCallback } from 'react';
import { isNil, negate } from 'lodash';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { EquityAllocationCellCustomData } from 'pages/ValuationsAllocation/allocation/EquityAllocation/config';
import { ValuationSummaryCellCustomData } from 'pages/ValuationsAllocation/approaches/ValuationSummary/config';
import { ChangedCell, OnSpreadsheetChange, OnWorkbookChange } from 'pages/ValuationsAllocation/types';
import {
  handleAllocationMethod,
  handleCapTableSelection,
  handleEquityValue,
  handleMaturity,
  handleScenarioWeight,
  handleScenarioWeightingProbability,
  handleVolatility,
} from './handlers';
import { UseEquityAllocationInputHandlersParams, UseEquityAllocationInputHandlersReturn } from './types';

/**
 * Custom hook that encapsulates all workbook cell change handlers for equity allocation inputs.
 * Provides a centralized place for handling spreadsheet changes related to:
 * - Scenario weights
 * - Allocation methods
 * - Cap table selections
 * - Maturity rates
 * - Volatility values
 * - Scenario weighting probabilities
 * - Equity values
 *
 * @param params - Configuration and callbacks for handling cell changes
 * @returns Handlers for spreadsheet changes
 */
const useEquityAllocationInputHandlers = (
  params: UseEquityAllocationInputHandlersParams
): UseEquityAllocationInputHandlersReturn => {
  const {
    allocationScenarios,
    approaches,
    cells,
    onChange,
    updateAllocationScenariosWithRelated,
    updateMainSpreadsheets,
    updateOPMInputs,
    updateSecurities,
    updateAllocationValues,
    handleAddApproach,
    expandOPMInputs,
    collapseOPMInputs,
    setIsUpdatingSpreadsheets,
    handleEquityValueChange,
    setChangedCells,
  } = params;

  /**
   * Main workbook change handler that orchestrates all cell change handlers.
   * Processes cell changes in the following order:
   * 1. Equity value changes
   * 2. Scenario weight changes (an approach changing the weight in a given scenario)
   * 3. Allocation method changes
   * 4. Cap table selection changes
   * 5. Maturity changes
   * 6. Volatility changes
   * 7. Scenario weighting probability changes
   * 8. Equity value recalculation
   */
  const onWorkbookChange: OnWorkbookChange = useCallback(
    async (cell, expr, customFn) => {
      handleEquityValue({
        params: {
          cell,
          customFn,
          expr,
        },
        approaches,
        onChange,
        updateAllocationValues,
      });

      // Start Updating Spreadsheets
      setIsUpdatingSpreadsheets(true);

      try {
        // this one is for the weight an approach has in a scenario
        await handleScenarioWeight({
          cell: cell as ICell<ValuationSummaryCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          updateAllocationScenariosWithRelated,
        });

        await handleAllocationMethod({
          cell: cell as ICell<EquityAllocationCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          expandOPMInputs,
          collapseOPMInputs,
          handleAddApproach,
          updateAllocationScenariosWithRelated,
          updateMainSpreadsheets,
          updateOPMInputs,
          updateSecurities,
        });

        await handleCapTableSelection({
          cell: cell as ICell<EquityAllocationCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          updateAllocationScenariosWithRelated,
          updateMainSpreadsheets,
          updateSecurities,
        });

        await handleMaturity({
          cell: cell as ICell<EquityAllocationCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          updateAllocationScenariosWithRelated,
          updateOPMInputs,
        });

        await handleVolatility({
          cell: cell as ICell<EquityAllocationCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          updateAllocationScenariosWithRelated,
        });

        await handleScenarioWeightingProbability({
          cell: cell as ICell<ValuationSummaryCellCustomData | EquityAllocationCellCustomData>,
          allocationScenarios,
          approaches,
          cells,
          onChange,
          updateAllocationScenariosWithRelated,
        });

        // Handle changes to any equity value
        await handleEquityValueChange();
      } finally {
        // Always release the updating flag so save actions do not stay disabled after a failed recalculation.
        setIsUpdatingSpreadsheets(false);
      }
    },
    [
      allocationScenarios,
      approaches,
      cells,
      collapseOPMInputs,
      expandOPMInputs,
      handleAddApproach,
      handleEquityValueChange,
      onChange,
      setIsUpdatingSpreadsheets,
      updateAllocationScenariosWithRelated,
      updateAllocationValues,
      updateMainSpreadsheets,
      updateOPMInputs,
      updateSecurities,
    ]
  );

  /**
   * Update changed cells by filtering out null/undefined values.
   */
  const updateChangedCells = useCallback(
    (currentChangedCells: ChangedCell[]) => {
      setChangedCells(currentChangedCells?.filter(negate(isNil)));
    },
    [setChangedCells]
  );

  /**
   * Spreadsheet change handler that wraps onWorkbookChange with changed cells tracking.
   */
  const handleOnSpreadsheetChange: OnSpreadsheetChange = useCallback(
    async (cell, expr) => {
      await onWorkbookChange(cell, expr, updateChangedCells);
    },
    [onWorkbookChange, updateChangedCells]
  );

  return {
    handleOnSpreadsheetChange,
    onWorkbookChange,
  };
};

export default useEquityAllocationInputHandlers;
